import { useTranslation } from 'react-i18next';
import { Cloud, CloudOff, Loader } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { useOnlineStatus } from '../application/use-online-status';
import { usePendingSyncCount } from '../application/use-pending-sync';

/**
 * Tiny status pill that lives in the workout-session header. Three states:
 *  - online & queue empty  → "Синхронизировано" (green dot + cloud)
 *  - online & queue > 0    → "N не отправлено" (amber dot + spinner)
 *  - offline               → "Офлайн" (red dot + cloud-off)
 */
export function SyncIndicator(): React.JSX.Element {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const pending = usePendingSyncCount();

  const variant = !online ? 'offline' : pending > 0 ? 'pending' : 'synced';

  const text =
    variant === 'offline'
      ? t('sync.offline')
      : variant === 'pending'
        ? t('sync.pending', { count: pending })
        : t('sync.synced');

  const Icon = variant === 'offline' ? CloudOff : variant === 'pending' ? Loader : Cloud;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-medium',
        variant === 'offline' && 'border-destructive/40 bg-destructive/10 text-destructive',
        variant === 'pending' && 'border-amber-500/40 bg-amber-500/10 text-amber-300',
        variant === 'synced' && 'border-border bg-muted text-muted-foreground',
      )}
      role="status"
      aria-live="polite"
    >
      <Icon className={cn('size-3', variant === 'pending' && 'animate-spin')} aria-hidden />
      <span>{text}</span>
    </div>
  );
}
